import type { SystemReview } from "@scoach/types";
import { useState } from "react";

import { reviewsApi } from "./api.ts";

function timeAgo(iso: string): string {
  const ms = Date.now() - Date.parse(iso);
  const m = Math.floor(ms / 60_000);
  if (Number.isNaN(m) || m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

/**
 * A single review's user↔admin conversation. `viewerSide` decides which bubbles
 * sit on the right (the current viewer's own messages) and what a new message is
 * posted as. Calls `onChange` with the server's updated review after a reply.
 */
export function ReviewThread({
  review,
  viewerSide,
  onChange,
}: {
  review: SystemReview;
  viewerSide: "user" | "admin";
  onChange?: (r: SystemReview) => void;
}) {
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState("");
  const messages = review.messages ?? [];
  const isAdmin = viewerSide === "admin";

  async function send() {
    const t = text.trim();
    if (!t || sending) return;
    setSending(true);
    try {
      const updated = await reviewsApi.postMessage(review.id, t);
      setText("");
      onChange?.(updated);
    } finally {
      setSending(false);
    }
  }

  async function saveEdit(msgId: string) {
    const t = editText.trim();
    if (!t) return;
    const updated = await reviewsApi.editMessage(review.id, msgId, t).catch(() => null);
    setEditingId(null);
    setEditText("");
    if (updated) onChange?.(updated);
  }

  async function removeMessage(msgId: string) {
    if (!window.confirm("Remove this answer?")) return;
    const updated = await reviewsApi.deleteMessage(review.id, msgId).catch(() => null);
    if (updated) onChange?.(updated);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ fontSize: 12, color: "var(--text-3)" }}>
        {"★".repeat(review.rating)}
        {"☆".repeat(Math.max(0, 5 - review.rating))} · {review.name || review.email}
      </div>

      {messages.length === 0 && !review.text ? (
        <div style={{ fontSize: 13, color: "var(--text-4)" }}>
          Rating only — no comment. Reply to start a conversation.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {messages.map((m) => {
            const mine = m.from === viewerSide;
            return (
              <div
                key={m.id}
                style={{
                  alignSelf: mine ? "flex-end" : "flex-start",
                  maxWidth: "80%",
                  background: m.from === "admin" ? "var(--gc-blue-50, #e8f0fe)" : "var(--bg-2)",
                  border: "1px solid var(--border-soft)",
                  borderRadius: 10,
                  padding: "6px 10px",
                }}
              >
                <div style={{ fontSize: 11, color: "var(--text-3)", marginBottom: 2 }}>
                  {m.from === "admin" ? "SellSynq admin" : m.authorName || "You"} · {timeAgo(m.createdAt)}
                  {m.editedAt ? " · edited" : ""}
                </div>
                {editingId === m.id ? (
                  <div>
                    <textarea
                      dir="auto"
                      value={editText}
                      autoFocus
                      rows={2}
                      onChange={(e) => setEditText(e.target.value)}
                      style={{
                        width: "100%",
                        fontSize: 13,
                        borderRadius: 8,
                        border: "1px solid var(--border)",
                        padding: "6px 8px",
                        background: "var(--surface)",
                        color: "var(--text-1)",
                        resize: "vertical",
                      }}
                    />
                    <div style={{ display: "flex", gap: 6, marginTop: 4, justifyContent: "flex-end" }}>
                      <button
                        type="button"
                        className="ghost-btn xs"
                        onClick={() => setEditingId(null)}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="pill-btn xs"
                        onClick={() => void saveEdit(m.id)}
                      >
                        Save
                      </button>
                    </div>
                  </div>
                ) : (
                  <div
                    dir="auto"
                    style={{ fontSize: 13, color: "var(--text-1)", whiteSpace: "pre-wrap" }}
                  >
                    {m.text}
                  </div>
                )}
                {isAdmin && m.from === "admin" && editingId !== m.id && (
                  <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                    <button
                      type="button"
                      className="ghost-btn xs"
                      onClick={() => {
                        setEditingId(m.id);
                        setEditText(m.text);
                      }}
                      style={{ fontSize: 11 }}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="ghost-btn xs"
                      onClick={() => void removeMessage(m.id)}
                      style={{ fontSize: 11, color: "var(--gc-red, #d93025)" }}
                    >
                      Remove
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void send();
          }}
          placeholder={viewerSide === "admin" ? "Reply to this user…" : "Reply to SellSynq…"}
          style={{
            flex: 1,
            padding: "8px 10px",
            borderRadius: 8,
            border: "1px solid var(--border)",
            fontSize: 13,
            background: "var(--surface)",
            color: "var(--text-1)",
          }}
        />
        <button type="button" className="pill-btn" disabled={sending || !text.trim()} onClick={send}>
          {sending ? "Sending…" : "Send"}
        </button>
      </div>
    </div>
  );
}
